import React, { useEffect, useRef, useState } from 'react';
import { db, collection, getDocs, query, where, limit } from './firebase';
import { motion } from 'framer-motion';

const AdSense = ({ slot, format = 'auto', responsive = true, className = '', style = {}, width, height }) => {
  const adRef = useRef(null);
  const [adLoaded, setAdLoaded] = useState(false);
  const [adFailed, setAdFailed] = useState(false);
  const [fallbackAd, setFallbackAd] = useState(null);

  const adClient = 'ca-pub-2887238820446530';
  const adSlot = slot || process.env.REACT_APP_ADSENSE_AD_SLOT || '3902042360';

  // Proactive Fallback Fetching
  useEffect(() => {
    const fetchFallback = async () => {
      try {
        let q = query(
          collection(db, 'ads'),
          where('isActive', '==', true)
        );

        const snap = await getDocs(q);
        if (!snap.empty) {
          // Find the best match locally if width/height are specified, otherwise take the first
          let ads = snap.docs.map(d => d.data());
          if (width || height) {
            const bestMatch = ads.find(a =>
              (width && a.width === width) || (height && a.height === height)
            );
            setFallbackAd(bestMatch || ads[0]);
          } else {
            setFallbackAd(ads.find(a => a.type === (format === 'fluid' ? 'infeed' : 'banner')) || ads[0]);
          }
        }
      } catch (err) {
        console.error("Fallback ad fetch error:", err);
      }
    };
    fetchFallback();
  }, [format, width, height]);

  // Handle AdSense Loading/Failure
  useEffect(() => {
    let timer;
    try {
      if (window && adRef.current) {
        const adEl = adRef.current.querySelector('.adsbygoogle');
        if (adEl) {
          // Push to adsbygoogle
          (window.adsbygoogle = window.adsbygoogle || []).push({});

          // Detect if ad actually loads or gets blocked
          timer = setTimeout(() => {
            const status = adEl.getAttribute('data-adsbygoogle-status');
            const hasHeight = adEl.offsetHeight > 0;

            if (status === 'done' && hasHeight) {
              setAdLoaded(true);
              setAdFailed(false);
            } else {
              setAdFailed(true);
            }
          }, 3500);
        }
      }
    } catch (e) {
      setAdFailed(true);
    }
    return () => clearTimeout(timer);
  }, []);

  // Show Fallback if Google Ad failed
  if (adFailed && fallbackAd) {
    return (
      <motion.div
        initial={{ opacity: 0, scale: 0.98 }}
        animate={{ opacity: 1, scale: 1 }}
        className={`snaped-fallback-ad ${className}`}
        style={{
          width: width ? `${width}px` : '100%',
          height: height ? `${height}px` : 'auto',
          maxWidth: '100%',
          margin: '24px auto',
          borderRadius: '16px',
          overflow: 'hidden',
          cursor: 'pointer',
          position: 'relative',
          border: '1px solid rgba(255,255,255,0.08)',
          background: 'rgba(255,255,255,0.03)',
          backdropFilter: 'blur(12px)',
          boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
          ...style
        }}
        onClick={() => window.open(fallbackAd.targetUrl, '_blank')}
      >
        <img
          src={fallbackAd.imageUrl}
          alt={fallbackAd.title || "SnapED codeCampus Advertisement"}
          style={{
            width: '100%',
            height: height ? '100%' : 'auto',
            display: 'block',
            maxHeight: height ? 'none' : '400px',
            objectFit: 'cover'
          }}
        />
        <div style={{
          position: 'absolute',
          top: '12px',
          right: '12px',
          padding: '4px 10px',
          background: 'rgba(34,197,94,0.9)',
          borderRadius: '6px',
          fontSize: '10px',
          color: '#000',
          fontWeight: 900,
          letterSpacing: '0.05em',
          backdropFilter: 'blur(4px)',
          boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
          pointerEvents: 'none',
          textTransform: 'uppercase'
        }}>
          Ad
        </div>
      </motion.div>
    );
  }

  if (adFailed && !fallbackAd) return null;

  return (
    <div
      ref={adRef}
      className={`snaped-ad-container ${className}`}
      style={{
        width: width ? `${width}px` : '100%',
        maxWidth: '100%',
        overflow: 'hidden',
        display: 'flex',
        justifyContent: 'center',
        padding: adLoaded ? '16px 0' : '0',
        margin: adLoaded ? '16px 0' : '0',
        minHeight: adLoaded ? 'auto' : 0,
        transition: 'all 0.3s ease',
        ...style
      }}
    >
      <ins
        className="adsbygoogle"
        style={{
          display: 'block',
          width: width ? `${width}px` : '100%',
          height: height ? `${height}px` : 'auto',
          maxWidth: '1200px',
          borderRadius: '12px',
          overflow: 'hidden'
        }}
        data-ad-client={adClient}
        data-ad-slot={adSlot}
        data-ad-format={format}
        data-full-width-responsive={responsive ? 'true' : 'false'}
      />
    </div>
  );
};

// Specific ad placements optimized for UX + earnings
export const BannerAd = () => (
  <AdSense
    format="auto"
    responsive={true}
    className="banner-ad"
  />
);

export const InFeedAd = () => (
  <AdSense
    format="fluid"
    className="infeed-ad"
  />
);

export const SidebarAd = () => (
  <AdSense
    format="auto"
    responsive={true}
    className="sidebar-ad"
  />
);

export default AdSense;
